import { objectOf, arrayOf, anyOf, maybe, recordOf, enumOf } from "@altostra/type-validations";
import { nullValidation, number, string } from "@altostra/type-validations/lib/primitives";

import { workspaceAlertTypes } from "./constants";
import type {
  ProductAlertCondition,
  ProductAlertConditionGroup,
  ProductAlertRecipientGroup,
  ProductAlertRules,
  ProductAlertStatus,
  ProductAlertThresholdUnit,
  WorkspaceAlertType,
} from "./types";
import { productAlertThresholdUnitPercentage, productAlertThresholdUnitVolume } from "./types";

const isProductAlertStatus = enumOf<ProductAlertStatus>("active", "disabled");
const isWorkspaceAlertType = enumOf<WorkspaceAlertType>(...workspaceAlertTypes);
const isProductAlertRecipientGroup = enumOf<ProductAlertRecipientGroup>("all_admins", "object_editors");
const isProductAlertThresholdUnit = enumOf<ProductAlertThresholdUnit>(
  productAlertThresholdUnitVolume,
  productAlertThresholdUnitPercentage
);

const productAlertNotificationSettingsShape = {
  email: maybe(
    objectOf({
      recipients: arrayOf(string),
      recipient_groups: maybe(arrayOf(isProductAlertRecipientGroup)),
    })
  ),
  slack: maybe(
    objectOf({
      url: string,
    })
  ),
  webhook: maybe(
    objectOf({
      url: string,
      headers: maybe(recordOf(string)),
    })
  ),
};

// Workspace alerts (target "app_group") have no target_name, and the
// CANVAS_ARCHIVED type has no metric rules or periodic schedule, so those
// fields are null on it even though per-Canvas alerts always set them.
const productAlertScheduleShape = {
  schedule_type: string,
  schedule_time_seconds: anyOf(number, nullValidation),
  lookback_seconds: anyOf(number, nullValidation),
  schedule_anchor_seconds: maybe(number),
};

const isProductAlertCondition = objectOf<ProductAlertCondition>({
  metric: string,
  subject: string,
  operator: string,
  threshold_value: number,
  threshold_unit: isProductAlertThresholdUnit,
});

const isProductAlertRules = objectOf<ProductAlertRules>({
  group_operator: anyOf(string, nullValidation),
  condition_groups: arrayOf(
    objectOf<ProductAlertConditionGroup>({
      condition_operator: string,
      conditions: arrayOf(isProductAlertCondition),
    })
  ),
});

const productAlertResultItemBaseShape = {
  id: string,
  target: string,
  target_id: string,
  target_name: anyOf(string, nullValidation),
  alert_type: maybe(isWorkspaceAlertType, true),
  covered_canvas_count: maybe(number),
  name: string,
  status: isProductAlertStatus,
  rules: isProductAlertRules,
  schedule: objectOf(productAlertScheduleShape),
  notification_channels: arrayOf(string),
  updated_at: number,
};

const productAlertNotificationSettingsValidator = objectOf(productAlertNotificationSettingsShape);

export const isProductAlertResultItem = objectOf({
  ...productAlertResultItemBaseShape,
  notification_settings: productAlertNotificationSettingsValidator,
});

export const isProductAlertsResult = objectOf({
  hits: number,
  results: arrayOf(
    objectOf({
      ...productAlertResultItemBaseShape,
      notification_settings: maybe(productAlertNotificationSettingsValidator),
    })
  ),
});
